import { useMemo, type MutableRefObject } from 'react';
import type { VirtualItem } from '@tanstack/virtual-core';
import { fg } from '@atlassian/jira-feature-gating';
import { ROW_HEIGHT } from '../../../common/constants.tsx';
import { useScrollElement } from '../../../controllers/scroll-state/index.tsx';
import { useSSRSafeVirtualizer } from '../../../controllers/virtualization/index.tsx';
import {
	useVirtualisationActions,
	type VirtualItemsMap,
} from '../../../controllers/virtualization/main.tsx';

export type Props = {
	rows: number;
	// make setTableHeight required and remove totalSizeRef when cleaning up jira_list_virtualizer_table_height
	totalSizeRef: MutableRefObject<number>;
	setTableHeight?: React.Dispatch<React.SetStateAction<number>>;
	getItemKey: (index: number) => number | string | bigint;
};

const estimateSize = () => ROW_HEIGHT + 1; // include border

export const InitializeVirtualizer = ({
	rows = 0,
	getItemKey,
	totalSizeRef,
	setTableHeight,
}: Props) => {
	const scrollElement = useScrollElement();
	const { setVirtualiser } = useVirtualisationActions();

	const options = useMemo(
		() => ({
			count: rows,
			getScrollElement: () => scrollElement,
			estimateSize,
			getItemKey,
			overscan: 10,
		}),
		[getItemKey, rows, scrollElement],
	);

	const virtualizer = useSSRSafeVirtualizer(options);
	const totalSize = virtualizer?.getTotalSize();
	const items = virtualizer?.getVirtualItems();

	if (fg('jira_list_virtualizer_table_height')) {
		setTableHeight && setTableHeight(totalSize ?? 0);
	} else {
		// eslint-disable-next-line no-param-reassign
		totalSizeRef.current = totalSize ?? 0;
	}

	const virtualItemsMap = items?.reduce((acc: VirtualItemsMap, item: VirtualItem) => {
		acc[String(item.key)] = true;
		return acc;
	}, {});

	setVirtualiser(virtualizer, virtualItemsMap);

	return null;
};
